import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
  parse,
  setWeek,
} from 'date-fns';
import { nl } from 'date-fns/locale';
import { supabase } from '../lib/supabase';
import { Coins } from 'lucide-react';

interface RevenueEntry {
  id: string;
  date: string;
  cash_revenue: number;
  card_revenue: number;
  invoice_revenue: number;
}

export default function WeekView() {
  const { year = '', week = '' } = useParams();
  const [entries, setEntries] = useState<Record<string, RevenueEntry>>({});
  const [tipsMap, setTipsMap] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [showTips, setShowTips] = useState(false);

  // Calculate the start and end of the week
  const weekDate = setWeek(new Date(parseInt(year), 0), parseInt(week), { weekStartsOn: 1 });
  const weekStart = startOfWeek(weekDate, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(weekDate, { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start: weekStart, end: weekEnd });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: settings } = await supabase
          .from('settings')
          .select('show_tips')
          .eq('user_id', user.id)
          .maybeSingle();

        if (settings) {
          setShowTips(settings.show_tips ?? true);
        }

        const { data, error } = await supabase
          .from('revenue_entries')
          .select('*')
          .gte('date', format(weekStart, 'yyyy-MM-dd'))
          .lte('date', format(weekEnd, 'yyyy-MM-dd'));

        if (!error && data) {
          const entriesMap = data.reduce((acc, entry) => {
            acc[entry.date] = entry;
            return acc;
          }, {} as Record<string, RevenueEntry>);
          setEntries(entriesMap);
        }

        if (settings?.show_tips ?? true) {
          const { data: tipsData } = await supabase
            .from('hidden_revenue')
            .select('date, amount')
            .gte('date', format(weekStart, 'yyyy-MM-dd'))
            .lte('date', format(weekEnd, 'yyyy-MM-dd'));

          if (tipsData) {
            const tips: Record<string, number> = {};
            tipsData.forEach(tip => {
              if (!tips[tip.date]) {
                tips[tip.date] = 0;
              }
              tips[tip.date] += tip.amount;
            });
            setTipsMap(tips);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [weekStart, weekEnd]);

  const totals = days.reduce(
    (acc, day) => {
      const dateStr = format(day, 'yyyy-MM-dd');
      const entry = entries[dateStr];
      const tips = showTips ? (tipsMap[dateStr] || 0) : 0;
      if (entry) {
        acc.cash += entry.cash_revenue;
        acc.card += entry.card_revenue;
        acc.invoice += entry.invoice_revenue;
        acc.total += entry.cash_revenue + entry.card_revenue + entry.invoice_revenue;
      }
      acc.tips += tips;
      return acc;
    },
    { cash: 0, card: 0, invoice: 0, total: 0, tips: 0 }
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-3 lg:space-y-0">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Week {week}, {year}
        </h1>
        <button
          onClick={() => setShowTips(!showTips)}
          className={`w-full lg:w-auto flex items-center justify-center px-4 py-2 rounded-md shadow-sm text-sm font-medium transition-colors ${
            showTips
              ? 'bg-green-500/20 dark:bg-green-500/30 text-green-700 dark:text-green-300'
              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
        >
          <Coins className="h-4 w-4 mr-2" />
          <span>Fooi {showTips ? 'verbergen' : 'tonen'}</span>
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/30">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                  Datum
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                  Dag
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                  Contant
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                  Pin
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                  Factuur
                </th>
                {showTips && (
                  <th className="px-6 py-4 text-right text-xs font-semibold text-red-500 dark:text-red-400 uppercase tracking-wider">
                    Fooi
                  </th>
                )}
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                  Totaal
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {days.map((day) => {
                const dateStr = format(day, 'yyyy-MM-dd');
                const entry = entries[dateStr];
                const tips = showTips ? (tipsMap[dateStr] || 0) : 0;
                const total = entry
                  ? entry.cash_revenue + entry.card_revenue + entry.invoice_revenue + tips
                  : tips;

                return (
                  <tr key={dateStr} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {format(day, 'dd/MM/yyyy')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {format(day, 'EEEE', { locale: nl })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100">
                      €{entry?.cash_revenue?.toFixed(2) || '0.00'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100">
                      €{entry?.card_revenue?.toFixed(2) || '0.00'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100">
                      €{entry?.invoice_revenue?.toFixed(2) || '0.00'}
                    </td>
                    {showTips && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-red-500 dark:text-red-400 font-medium">
                        €{tips.toFixed(2)}
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-blue-600 dark:text-blue-400">
                      €{total.toFixed(2)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-blue-50/50 dark:bg-blue-900/10">
                <td
                  colSpan={2}
                  className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-blue-800 dark:text-blue-200"
                >
                  Weektotalen
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-blue-800 dark:text-blue-200">
                  €{totals.cash.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-blue-800 dark:text-blue-200">
                  €{totals.card.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-blue-800 dark:text-blue-200">
                  €{totals.invoice.toFixed(2)}
                </td>
                {showTips && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-red-500 dark:text-red-400">
                    €{totals.tips.toFixed(2)}
                  </td>
                )}
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-bold text-blue-800 dark:text-blue-200">
                  €{(totals.total + totals.tips).toFixed(2)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}